import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import OperationPrintDetails from "components/Prints/Operations/OperationPrintDetails";
import OperationPrintHeaderDetails from "components/Prints/Operations/OperationPrintHeaderDetails";
import { OperationPrintTotals } from "components/Prints/Operations/OperationPrintTotal";
import { PrintLayout } from "components/Prints/PrintLayout";
import { useEffect, useMemo, useRef, useState } from "react";
import { MdPrint } from "react-icons/md";
import { useReactToPrint } from "react-to-print";
import { Client, Movement, Store, User } from "types";

type Props = {
  open: boolean;
  movements: Movement[];
  additionalMovements: Movement[];
  clients: Client[];
  stores: Store[];
  users: User[];
  onClose: () => void;
};

type MovementSectionProps = {
  movement: Movement;
  additionalMovements: Movement[];
  clientsById: Map<number, Client>;
  stores: Store[];
  users: User[];
  pageBreakBefore?: boolean;
};

function MovementSection({
  movement,
  additionalMovements,
  clientsById,
  stores,
  users,
  pageBreakBefore = false,
}: MovementSectionProps) {
  const client =
    movement.client ?? clientsById.get(movement.client_id) ?? null;

  return (
    <Box
      sx={
        pageBreakBefore
          ? { breakBefore: "page", pageBreakBefore: "always", pt: 2 }
          : undefined
      }
    >
      {pageBreakBefore && (
        <OperationPrintHeaderDetails movement={movement} stores={stores} />
      )}
      <Box py={pageBreakBefore ? 2 : 0}>
        <OperationPrintDetails
          client={client}
          movement={movement}
          users={users}
        />
      </Box>
      <OperationPrintTotals movement={movement} />
      {additionalMovements.map((additionalMovement) => (
        <Box
          key={additionalMovement.id}
          sx={{ breakBefore: "page", pageBreakBefore: "always", pt: 2 }}
        >
          <OperationPrintHeaderDetails
            movement={additionalMovement}
            stores={stores}
          />
          <Box py={2}>
            <OperationPrintDetails
              client={
                additionalMovement.client ??
                clientsById.get(additionalMovement.client_id) ??
                null
              }
              movement={additionalMovement}
              users={users}
            />
          </Box>
          <OperationPrintTotals movement={additionalMovement} />
        </Box>
      ))}
    </Box>
  );
}

export default function GeneratedIncomeMovementsDialog({
  open,
  movements,
  additionalMovements,
  clients,
  stores,
  users,
  onClose,
}: Props) {
  const printRef = useRef<HTMLDivElement | null>(null);
  const [movementsToPrint, setMovementsToPrint] = useState<Movement[]>([]);
  const [printPending, setPrintPending] = useState(false);

  const additionalMovementsByParentId = useMemo(() => {
    const grouped = new Map<number, Movement[]>();

    for (const movement of additionalMovements) {
      if (movement.movement_id == null) continue;

      const parentMovements = grouped.get(movement.movement_id) ?? [];
      parentMovements.push(movement);
      grouped.set(movement.movement_id, parentMovements);
    }

    return grouped;
  }, [additionalMovements]);
  const clientsById = useMemo(
    () => new Map(clients.map((client) => [client.id, client])),
    [clients],
  );

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    copyStyles: true,
  });

  useEffect(() => {
    if (!printPending || movementsToPrint.length === 0) return;

    handlePrint();
    setPrintPending(false);
  }, [handlePrint, movementsToPrint, printPending]);

  const requestPrint = (selectedMovements: Movement[]) => {
    setMovementsToPrint(selectedMovements);
    setPrintPending(true);
  };

  const firstMovement = movementsToPrint[0];

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Movimientos generados</DialogTitle>
        <DialogContent dividers>
          <List disablePadding>
            {movements.map((movement) => (
              <ListItem
                key={movement.id}
                divider
                secondaryAction={
                  <Button
                    onClick={() => requestPrint([movement])}
                    startIcon={<MdPrint />}
                  >
                    Imprimir
                  </Button>
                }
              >
                <ListItemText
                  primary={`Ingreso Nro. ${movement.id ?? "-"}`}
                  secondary={
                    movement.numero_remito
                      ? `Remito: ${movement.numero_remito}`
                      : undefined
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="outlined">
            Cerrar
          </Button>
          <Button
            onClick={() => requestPrint(movements)}
            variant="contained"
            startIcon={<MdPrint />}
            disabled={movements.length === 0}
          >
            Imprimir todos
          </Button>
        </DialogActions>
      </Dialog>

      {firstMovement && (
        <PrintLayout
          ref={printRef}
          headerInfo={
            <OperationPrintHeaderDetails
              movement={firstMovement}
              stores={stores}
            />
          }
        >
          <Box>
            {movementsToPrint.map((movement, index) => (
              <MovementSection
                key={movement.id}
                movement={movement}
                additionalMovements={
                  movement.id == null
                    ? []
                    : additionalMovementsByParentId.get(movement.id) ?? []
                }
                clientsById={clientsById}
                stores={stores}
                users={users}
                pageBreakBefore={index > 0}
              />
            ))}
          </Box>
        </PrintLayout>
      )}
    </>
  );
}
